import { useEffect, useState } from 'react';
import { adminApi } from '../../services/admin/admin.service';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { Loader2, Plus, Trash2, Save, ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';

export default function QuizEditor() {
    const [header, setHeader] = useState<any>(null);
    const [categories, setCategories] = useState<any[]>([]);
    const [questions, setQuestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedQ, setExpandedQ] = useState<number | null>(null);

    useEffect(() => {
        Promise.all([
            adminApi.getQuizHeader().then(setHeader),
            adminApi.getCategories().then(setCategories),
            adminApi.getQuizQuestions().then(setQuestions),
        ]).catch(e => toast.error('Erro: ' + e.message)).finally(() => setLoading(false));
    }, []);

    // ─── Header ───
    const [headerForm, setHeaderForm] = useState<any>({});
    const [savingHeader, setSavingHeader] = useState(false);
    useEffect(() => { if (header) setHeaderForm(header); }, [header]);

    const saveHeader = async () => {
        setSavingHeader(true);
        try { await adminApi.updateQuizHeader(headerForm); toast.success('Header salvo!'); } catch (e: any) { toast.error(e.message); } finally { setSavingHeader(false); }
    };

    // ─── Category editing ───
    const [editingCat, setEditingCat] = useState<number | null>(null);
    const [catForm, setCatForm] = useState<any>({});

    const saveCat = async () => {
        try {
            const updated = await adminApi.updateCategory(catForm.id, catForm);
            setCategories(categories.map(c => c.id === catForm.id ? updated : c));
            toast.success('Categoria salva!');
            setEditingCat(null);
        } catch (e: any) { toast.error(e.message); }
    };

    // ─── Question CRUD ───
    const [qForm, setQForm] = useState<any>({});
    const [editingQ, setEditingQ] = useState<number | null>(null);
    const [creatingQ, setCreatingQ] = useState(false);

    const saveQuestion = async () => {
        try {
            const result = await adminApi.upsertQuizQuestion(qForm);
            if (editingQ === -1) {
                setQuestions([...questions, { ...result, quiz_answers: [], categories: null }]);
            } else {
                setQuestions(questions.map(q => q.id === result.id ? { ...q, ...result } : q));
            }
            toast.success('Pergunta salva!');
            setEditingQ(null);
            setCreatingQ(false);
        } catch (e: any) { toast.error(e.message); }
    };

    const deleteQuestion = async (id: number) => {
        if (!confirm('Remover esta pergunta e todas as suas respostas?')) return;
        try {
            await adminApi.deleteQuizQuestion(id);
            setQuestions(questions.filter(q => q.id !== id));
            toast.success('Pergunta removida!');
        } catch (e: any) { toast.error(e.message); }
    };

    // ─── Answer CRUD ───
    const [ansForm, setAnsForm] = useState<any>({});
    const [editingAns, setEditingAns] = useState<number | null>(null);
    const [creatingAns, setCreatingAns] = useState<number | null>(null); // question_id

    const saveAnswer = async (questionId: number) => {
        try {
            const payload = { ...ansForm, question_id: questionId };
            const result = await adminApi.upsertQuizAnswer(payload);
            setQuestions(questions.map(q => {
                if (q.id !== questionId) return q;
                const answers = editingAns === -1
                    ? [...(q.quiz_answers || []), result]
                    : (q.quiz_answers || []).map((a: any) => a.id === result.id ? result : a);
                return { ...q, quiz_answers: answers };
            }));
            toast.success('Resposta salva!');
            setEditingAns(null); setCreatingAns(null); setAnsForm({});
        } catch (e: any) { toast.error(e.message); }
    };

    const deleteAnswer = async (answerId: number, questionId: number) => {
        if (!confirm('Remover esta resposta?')) return;
        try {
            await adminApi.deleteQuizAnswer(answerId);
            setQuestions(questions.map(q => {
                if (q.id !== questionId) return q;
                return { ...q, quiz_answers: (q.quiz_answers || []).filter((a: any) => a.id !== answerId) };
            }));
            toast.success('Resposta removida!');
        } catch (e: any) { toast.error(e.message); }
    };

    if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-green-600" /></div>;

    return (
        <div>
            <div className="flex items-center gap-3 mb-6">
                <Link to="/admin/dashboard"><ArrowLeft className="h-5 w-5 text-gray-500 hover:text-gray-800" /></Link>
                <h1 className="text-2xl font-bold">Editor do Quiz</h1>
            </div>

            <Tabs defaultValue="questions">
                <TabsList className="mb-4">
                    <TabsTrigger value="header">Header</TabsTrigger>
                    <TabsTrigger value="categories">Categorias</TabsTrigger>
                    <TabsTrigger value="questions">Perguntas ({questions.length})</TabsTrigger>
                </TabsList>

                {/* HEADER */}
                <TabsContent value="header">
                    <Card>
                        <CardHeader><CardTitle>Header do Quiz</CardTitle></CardHeader>
                        <CardContent className="space-y-3">
                            {[{ key: 'overline', label: 'Overline' }, { key: 'title', label: 'Título' }, { key: 'description', label: 'Descrição' }].map(f => (
                                <div key={f.key}>
                                    <label className="text-sm font-medium text-gray-600">{f.label}</label>
                                    {f.key === 'description' ? (
                                        <Textarea value={headerForm[f.key] || ''} onChange={e => setHeaderForm({ ...headerForm, [f.key]: e.target.value })} className="mt-1" />
                                    ) : (
                                        <Input value={headerForm[f.key] || ''} onChange={e => setHeaderForm({ ...headerForm, [f.key]: e.target.value })} className="mt-1" />
                                    )}
                                </div>
                            ))}
                            <Button onClick={saveHeader} disabled={savingHeader}>
                                {savingHeader ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}Salvar
                            </Button>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* CATEGORIES */}
                <TabsContent value="categories">
                    <Card>
                        <CardHeader><CardTitle>Categorias ({categories.length})</CardTitle></CardHeader>
                        <CardContent>
                            {categories.map(cat => (
                                <div key={cat.id} className="flex items-center gap-3 p-3 border rounded-lg mb-2">
                                    {editingCat === cat.id ? (
                                        <>
                                            <Input value={catForm.title || ''} onChange={e => setCatForm({ ...catForm, title: e.target.value })} className="flex-1" />
                                            <Button size="sm" onClick={saveCat}>Salvar</Button>
                                            <Button size="sm" variant="ghost" onClick={() => setEditingCat(null)}>Cancelar</Button>
                                        </>
                                    ) : (
                                        <>
                                            <span className="flex-1 font-medium">{cat.title}</span>
                                            <span className="text-xs text-gray-400">ID: {cat.id}</span>
                                            <Button size="sm" variant="ghost" onClick={() => { setEditingCat(cat.id); setCatForm(cat); }}>Editar</Button>
                                        </>
                                    )}
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* QUESTIONS */}
                <TabsContent value="questions">
                    <div className="flex justify-end mb-4">
                        <Button onClick={() => { setCreatingQ(true); setEditingQ(-1); setQForm({ order: questions.length + 1 }); }}>
                            <Plus className="h-4 w-4 mr-1" />Nova Pergunta
                        </Button>
                    </div>

                    {creatingQ && (
                        <Card className="mb-4 border-2 border-blue-200">
                            <CardContent className="pt-4 space-y-3">
                                <div>
                                    <label className="text-sm font-medium">Pergunta</label>
                                    <Input value={qForm.question || ''} onChange={e => setQForm({ ...qForm, question: e.target.value })} className="mt-1" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-sm font-medium">Ordem</label>
                                        <Input type="number" value={qForm.order ?? ''} onChange={e => setQForm({ ...qForm, order: Number(e.target.value) })} className="mt-1" />
                                    </div>
                                    <div>
                                        <label className="text-sm font-medium">Categoria</label>
                                        <select className="w-full mt-1 border rounded-md p-2 text-sm" value={qForm.category_id || ''} onChange={e => setQForm({ ...qForm, category_id: e.target.value ? Number(e.target.value) : null })}>
                                            <option value="">Sem categoria</option>
                                            {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div className="flex gap-2">
                                    <Button onClick={saveQuestion}><Save className="h-4 w-4 mr-1" />Salvar</Button>
                                    <Button variant="ghost" onClick={() => { setCreatingQ(false); setEditingQ(null); }}>Cancelar</Button>
                                </div>
                            </CardContent>
                        </Card>
                    )}

                    {questions.map(q => (
                        <Card key={q.id} className="mb-3">
                            <CardContent className="pt-4">
                                {/* Question Header */}
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex-1 cursor-pointer" onClick={() => setExpandedQ(expandedQ === q.id ? null : q.id)}>
                                        <div className="flex items-center gap-2">
                                            {expandedQ === q.id ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                                            <span className="text-sm text-gray-400 font-mono">#{q.order}</span>
                                            <span className="font-medium">{q.question}</span>
                                        </div>
                                        <div className="flex gap-2 mt-1 ml-6">
                                            {q.categories && <span className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">{q.categories.title}</span>}
                                            <span className="text-xs text-gray-400">{(q.quiz_answers || []).length} respostas</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1 shrink-0">
                                        {editingQ === q.id ? (
                                            <>
                                                <Button size="sm" onClick={saveQuestion}>Salvar</Button>
                                                <Button size="sm" variant="ghost" onClick={() => setEditingQ(null)}>Cancelar</Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => { setEditingQ(q.id); setQForm(q); }}>Editar</Button>
                                                <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteQuestion(q.id)}><Trash2 className="h-4 w-4" /></Button>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Inline edit question */}
                                {editingQ === q.id && (
                                    <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
                                        <Input value={qForm.question || ''} onChange={e => setQForm({ ...qForm, question: e.target.value })} placeholder="Texto da pergunta" />
                                        <div className="grid grid-cols-2 gap-3">
                                            <Input type="number" value={qForm.order ?? ''} onChange={e => setQForm({ ...qForm, order: Number(e.target.value) })} placeholder="Ordem" />
                                            <select className="border rounded-md p-2 text-sm" value={qForm.category_id || ''} onChange={e => setQForm({ ...qForm, category_id: e.target.value ? Number(e.target.value) : null })}>
                                                <option value="">Sem categoria</option>
                                                {categories.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                )}

                                {/* Answers (expanded) */}
                                {expandedQ === q.id && (
                                    <div className="mt-4 ml-6 space-y-2">
                                        <div className="flex items-center justify-between">
                                            <h4 className="text-sm font-semibold text-gray-600">Respostas</h4>
                                            <Button size="sm" variant="outline" onClick={() => { setCreatingAns(q.id); setEditingAns(-1); setAnsForm({ order: (q.quiz_answers || []).length + 1, score: 0 }); }}>
                                                <Plus className="h-3 w-3 mr-1" />Nova Resposta
                                            </Button>
                                        </div>
                                        {(q.quiz_answers || []).map((a: any) => (
                                            <div key={a.id} className="border rounded p-2">
                                                {editingAns === a.id ? (
                                                    <div className="space-y-2">
                                                        <Input value={ansForm.answer || ''} onChange={e => setAnsForm({ ...ansForm, answer: e.target.value })} placeholder="Resposta" />
                                                        <div className="grid grid-cols-2 gap-2">
                                                            <Input type="number" value={ansForm.score ?? ''} onChange={e => setAnsForm({ ...ansForm, score: Number(e.target.value) })} placeholder="Score" />
                                                            <Input type="number" value={ansForm.order ?? ''} onChange={e => setAnsForm({ ...ansForm, order: Number(e.target.value) })} placeholder="Ordem" />
                                                        </div>
                                                        <div className="flex gap-2">
                                                            <Button size="sm" onClick={() => saveAnswer(q.id)}>Salvar</Button>
                                                            <Button size="sm" variant="ghost" onClick={() => { setEditingAns(null); setAnsForm({}); }}>Cancelar</Button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center justify-between">
                                                        <div>
                                                            <span className="text-sm">{a.answer}</span>
                                                            <span className="ml-2 text-xs text-gray-400">score: {a.score ?? '—'} | ordem: {a.order}</span>
                                                        </div>
                                                        <div className="flex gap-1">
                                                            <Button size="sm" variant="ghost" onClick={() => { setEditingAns(a.id); setAnsForm(a); }}>Editar</Button>
                                                            <Button size="sm" variant="ghost" className="text-red-500" onClick={() => deleteAnswer(a.id, q.id)}><Trash2 className="h-3 w-3" /></Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {creatingAns === q.id && editingAns === -1 && (
                                            <div className="border-2 border-dashed border-green-200 rounded p-3 space-y-2">
                                                <Input value={ansForm.answer || ''} onChange={e => setAnsForm({ ...ansForm, answer: e.target.value })} placeholder="Nova resposta" />
                                                <div className="grid grid-cols-2 gap-2">
                                                    <Input type="number" value={ansForm.score ?? ''} onChange={e => setAnsForm({ ...ansForm, score: Number(e.target.value) })} placeholder="Score" />
                                                    <Input type="number" value={ansForm.order ?? ''} onChange={e => setAnsForm({ ...ansForm, order: Number(e.target.value) })} placeholder="Ordem" />
                                                </div>
                                                <div className="flex gap-2">
                                                    <Button size="sm" onClick={() => saveAnswer(q.id)}>Salvar</Button>
                                                    <Button size="sm" variant="ghost" onClick={() => { setCreatingAns(null); setEditingAns(null); setAnsForm({}); }}>Cancelar</Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    ))}
                </TabsContent>
            </Tabs>
        </div>
    );
}
